import { EnvsType, ReportCommentParameterType } from '@/types/SharedTypes';
import {
	FilterListType,
	FilterItem,
	Step,
	Operations,
	Operation,
	RapStatus,
} from '@/types/rapTypes';
import { getContrastRatio } from '@mui/material';
import dayjs from 'dayjs';
import { ParsedUrlQuery } from 'querystring';

export function formatDuration(duration: number) {
	const millisecondsPerMinute = 1000 * 60;
	const millisecondsPerHour = millisecondsPerMinute * 60;
	const millisecondsPerDay = millisecondsPerHour * 24;

	const days = Math.floor(duration / millisecondsPerDay);
	duration = duration % millisecondsPerDay;

	const hours = Math.floor(duration / millisecondsPerHour);
	duration = duration % millisecondsPerHour;

	const minutes = Math.floor(duration / millisecondsPerMinute);

	let result = '';
	if (days > 0) {
		result += `${days}d `;
	}
	if (hours > 0) {
		result += `${hours}h `;
	}
	if (minutes > 0) {
		result += `${minutes}m`;
	}

	return result.trim();
}

export const truncate = (
	textToTruncate: string | null,
	truncateFrom: number
) => {
	let truncatedString = textToTruncate;

	if (truncatedString && truncatedString?.length > truncateFrom) {
		truncatedString = truncatedString?.slice(0, truncateFrom);
		truncatedString = `${truncatedString}...`;
	}

	return truncatedString;
};

export const formatToMillisecond = (number: number): number => {
	if (number?.toString().length === 10) {
		return number * 1000;
	}

	return number;
};

export const getDate = (date: number): string =>
	dayjs(formatToMillisecond(date)).format('DD.MM.YYYY');

export const getDateTime = (date: number, wholeYear = false): string =>
	dayjs(formatToMillisecond(date)).format(
		!wholeYear ? 'DD MMM YY HH:mm' : 'DD MMM YYYY HH:mm'
	);

export const getAddedDsvToStart = (startTimestamp: number, dsv: number) => {
	if (typeof dsv !== 'number') return;
	const convertedDsvToTimestamp = dsv * 3.6e6;
	return getDateTime(convertedDsvToTimestamp + startTimestamp);
};

export function paginate<T>(
	array: Array<T>,
	page_size: number,
	page_number: number
): Array<T> {
	return array.slice((page_number - 1) * page_size, page_number * page_size);
}

export function numberToLetters(n: number): string {
	const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
	const base = alphabet.length;
	let result = '';

	while (n > 0) {
		n -= 1; // Decrease the number by 1 to fit the 0-based index
		const remainder = n % base;
		result = alphabet.charAt(remainder) + result;
		n = Math.floor(n / base);
	}

	return result;
}
export const uniqueId = () => {
	const dateString = Date.now().toString(36);
	const randomness = Math.random().toString(36);
	return dateString + randomness;
};

export const stringToColor = (string: string) => {
	let hash = 0;
	let i;

	for (i = 0; i < string.length; i += 1) {
		hash = string.charCodeAt(i) + ((hash << 5) - hash);
	}

	let color = '#';

	for (i = 0; i < 3; i += 1) {
		const value = (hash >> (i * 8)) & 0xff;
		color += `00${value.toString(16)}`.slice(-2);
	}

	return color;
};

export const unnamedString = {
	rap: 'Unnamed rap',
	operation: 'Unnamed operation',
	step: 'Unnamed step',
	check: 'Unnamed check',
};

export const statuses = {
	abandoned: 'rapstatus-abandoned',
	offshoreDraft: 'offshore-draft',
	approved: 'rapstatus-approved',
	underRevision: 'under-revision',
	underExecution: 'under-execution',
	completed: 'rapstatus-completed',
	started: 'rapstatus-started', // This is not a real status
	notStarted: 'rapstatus-not-started', // This is not a real status
};

export const statusLabels = {
	abandoned: 'Abandoned',
	offshoreDraft: 'Offshore Draft',
	approved: 'Final Approved',
	underApproval: 'Under Revision',
	underExecution: 'Under execution',
	completed: 'Completed',
	started: 'Started',
	notStarted: 'Not Started',
};

export const rapStatuses = [
	{
		name: 'All',
		uuid: 'all-regular',
	},

	{
		name: statusLabels.offshoreDraft,
		uuid: statuses.offshoreDraft,
	},
	{
		name: statusLabels.approved,
		uuid: statuses.approved,
	},
	{
		name: statusLabels.underApproval,
		uuid: statuses.underRevision,
	},
	{
		name: statusLabels.underExecution,
		uuid: statuses.underExecution,
	},
];

export const archivedRapStatuses = [
	{
		name: 'All',
		uuid: 'all-archived',
	},
	{
		name: statusLabels.abandoned,
		uuid: statuses.abandoned,
	},
	{
		name: statusLabels.completed,
		uuid: statuses.completed,
	},
];

export type FilterTemplateType = {
	rig: FilterItem[];
	status: FilterItem[];
	well: FilterItem[];
	timeplan: FilterItem[];
	wellbore: FilterItem[];
};

export const filtersTemplate: FilterTemplateType = {
	rig: [],
	status: [],
	well: [],
	timeplan: [],
	wellbore: [],
};

export const parseInputToValidStringValue = (
	input: string,
	allowNegative = false
) => {
	const numberPattern = allowNegative ? /^-?\d*([.,]\d*)?$/ : /^\d*([.,]\d*)?$/;
	const justCommaPattern = /^[,\.]/;
	const moreThanTwoDecimalsPattern = /(\.\d{2})\d+$/;
	if (!numberPattern.test(input)) {
		return null;
	}
	if (justCommaPattern.test(input)) {
		return null;
	}

	let value = input.replace(',', '.');

	if (moreThanTwoDecimalsPattern.test(input)) {
		return null;
	}

	return value;
};

export const findUnitName = (
	unitName: string,
	customName: string | null
): string => {
	if (unitName === 'other') {
		return customName as string;
	}
	return unitName;
};

export const filtersToString = (filters: FilterListType) => {
	let stringQuery = '';
	for (const [key, value] of Object.entries(filters)) {
		if (value.length) {
			const findSlug =
				slugList.find(elem => {
					if (elem.name === key) {
						return elem.slug;
					}
				})?.slug || key;
			stringQuery += stringQuery.length ? `&${findSlug}=` : `${findSlug}=`;
			value.forEach((elem, index) => {
				stringQuery += `${index > 0 ? '|' : ''}${elem.uuid}`;
			});
		}
	}

	return stringQuery;
};

const slugList = [
	{
		name: 'rig',
		slug: 'rig',
	},
	{
		name: 'timeplan',
		slug: 'timeplan',
	},
	{
		name: 'status',
		slug: 'status',
	},
	{
		name: 'well',
		slug: 'well',
	},
];

// If this is supposed to be client specific, it could be set as an env variable.
export const defaultSearchParams =
	'status=offshore-draft|rapstatus-inreview|rapstatus-approved|under-revision|under-execution&page=1&perPage=50';

export const defaultSearchParamsToRap =
	'status=offshore-draft|rapstatus-approved|under-revision|under-execution|rapstatus-inreview';
export const defaultArchiveSearchParamsToRap =
	'status=rapstatus-abandoned|rapstatus-completed';
export const defaultFilters: FilterTemplateType = {
	rig: [],
	status: [],
	well: [],
	timeplan: [],
	wellbore: [],
};

export const generateSearchStringFromQuery = (query: ParsedUrlQuery) => {
	let queryString = '&';

	if (Object.keys(query).length > 0) {
		for (const [key, value] of Object.entries(query)) {
			queryString = queryString.concat(`${key}=${value}&`);
		}
	}
	queryString = queryString.slice(0, -1);

	return queryString;
};

/* 	This function cleans up the filter if the statuses under-revision and under-exection is selected
	reason for this is that we do not have these filters available at the moment.
*/
export const filterCleanUp = (queryString: string) => {
	let cleanedFilter = queryString;
	if (cleanedFilter.includes('all')) {
		cleanedFilter = cleanedFilter.replace(
			'all-regular',
			'offshore-draft|rapstatus-inreview|rapstatus-approved|under-revision|under-execution'
		);
		cleanedFilter = cleanedFilter.replace(
			'all-archived',
			'rapstatus-abandoned|rapstatus-completed'
		);
	}
	if (!cleanedFilter.includes('status')) {
		cleanedFilter = cleanedFilter.concat(
			'&status=offshore-draft|rapstatus-inreview|rapstatus-approved|under-revision|under-execution'
		);
	}
	return cleanedFilter;
};

// This function is used to check if the test are running in a browser or in node
// If the tests are running in node, the url needs to be prepended with localhost:3000 or localhost:8000
export const prependUrl = (
	url: string,
	type: 'internal' | 'external' = 'external'
) => {
	if (isInTestEnv()) {
		if (type === 'external') {
			return `http://localhost:8000${url}`;
		}
		return `http://localhost:3000${url}`;
	}
	return url;
};

export const isInTestEnv = () => {
	return process.env.NODE_ENV === 'test';
};

// This function checks if the user is using a mobile device
export const isMobile = () => {
	if (typeof window === 'undefined') return false;
	const regex = /Android|webOS|iPhone|iPad|iPod/i;
	if (typeof window === 'undefined') return false;
	return regex.test(navigator?.userAgent);
};

export const userInitials = (name: string) => {
	const names = name.split(' ');
	let initials = names[0].substring(0, 1).toUpperCase();
	if (names.length > 1) {
		initials += names[names.length - 1].substring(0, 1).toUpperCase();
	}
	return initials;
};

export const predefinedRigSteps = [
	{ title: 'Clean & Clear drillfloor' },
	{ title: 'Dropscheck' },
	{ title: 'Flowcheck' },
	{ title: 'Repair hydraulic leaks' },
	{ title: 'Time out' },
	{ title: 'Waiting on weather' },
	{ title: 'Continue Drilling Ahead' },
	{ title: 'Circulate and Condition Mud' },
	{ title: 'Circulate Bottoms Up, Flow Check, and POOH' },
	{ title: 'Circulate and Condition Mud while Troubleshooting Equipment' },
	{ title: 'Run In Hole' },
];

export const fetchProcedurePdf = async (
	rapUuid: string,
	envs: EnvsType,
	bearerToken: string,
	setSnackbarState: any
): Promise<Response | undefined> => {
	// get the timezone from the client
	const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	try {
		const res = await fetch(
			`${envs.backendApiUrl}/api/pdf/download/${rapUuid}?clientTimeZone=${clientTimeZone}`,
			{
				headers: {
					Authorization: `Bearer ${bearerToken}`,
				},
			}
		);

		if (!res.ok) {
			const data = await res.json();
			throw new Error(`${res.status}: ${data.message}`);
		}

		return res;
	} catch (error: any) {
		setSnackbarState({
			open: true,
			variant: 'error',
			message: error.message,
		});
	}
};

export const findLatestDepth = (
	currentStepUuid: string,
	operations: Operations
): string => {
	let latestDepth = '0';
	let allSteps: Step[] = [];

	operations.forEach(operation => {
		const sortedSteps = sortArray(operation.steps as Step[], 'sorting');
		allSteps = [...allSteps, ...sortedSteps];
	});

	const currentStepIndex = allSteps.findIndex(
		step => step.uuid === currentStepUuid
	);

	if (currentStepIndex === 0) {
		return latestDepth;
	}

	const beforeSteps = allSteps.slice(0, currentStepIndex);

	for (let i = beforeSteps.length - 1; i >= 0; i--) {
		const prevStep = beforeSteps[i];
		if (prevStep.stepstatus === 'stepstatus-notapplicable') continue;
		if (prevStep.actualDepth || prevStep.actualDepth === 0) {
			latestDepth = prevStep.actualDepth.toString();
			break;
		}
	}

	return latestDepth;
};

export const sortArray = <T>(
	nodeArray: T[],
	sortField: keyof T,
	sorting: 'asc' | 'des' = 'asc'
): T[] => {
	return nodeArray?.sort((a, b) => {
		switch (sorting) {
			case 'asc': {
				if (a[sortField] < b[sortField]) {
					return -1;
				}
				if (a[sortField] > b[sortField]) {
					return 1;
				}
				return 0;
			}
			case 'des': {
				if (a[sortField] > b[sortField]) {
					return -1;
				}
				if (a[sortField] < b[sortField]) {
					return 1;
				}
				return 0;
			}
		}
	});
};

export const reportCommentParameters: Pick<
	ReportCommentParameterType,
	'description' | 'unitName' | 'unit'
>[] = [
	{
		description: 'WOB',
		unitName: 'ton',
		unit: 'beed8cd7-13df-5227-bd74-58ecd3b1087f',
	},
	{
		description: 'ROP',
		unitName: 'm/h',
		unit: '110607bd-bbdf-5c8d-b9fc-046bbca2f05a',
	},
	{
		description: 'RPM',
		unitName: 'rpm',
		unit: 'c7238db4-85ac-55ef-bd44-6f2167b7bb19',
	},
	{
		description: 'Torque',
		unitName: 'Nm',
		unit: '7b295e14-793a-5ed4-812f-58d29100206a',
	},
	{
		description: 'Flow',
		unitName: 'lpm',
		unit: '99c59d71-5fed-5232-9bbc-ae2523f867a4',
	},
	{
		description: 'ECD',
		unitName: 'sg',
		unit: 'ce4801e0-67d3-5431-abce-d51534c944c3',
	},
	{
		description: 'SPP',
		unitName: 'bar',
		unit: '5efb8e19-9049-5dee-8ee6-ba6898b0b3bc',
	},
];

// finds uuids for active operation and step, just first operation
// if no operation is active.
export const findActiveUuids = (operations: Operation[]) => {
	if (!operations) return [];
	const uuidArray: string[] = [];
	const sortedOperations = sortArray(operations, 'operationalstatus');
	const activeOperation = sortedOperations.find(
		operation => operation.operationalstatus === 'operationalstatus-started'
	);

	if (!activeOperation) {
		const [firstOperation] = sortedOperations;
		uuidArray.push(firstOperation.uuid);
		return uuidArray;
	}

	const activeStep = activeOperation.steps?.find(
		step => step.stepstatus === 'stepstatus-started'
	) as Step;

	return [activeOperation.uuid, activeStep.uuid];
};
/* 
 	this function checks the contrast value between the color a node and the background color that is given. 
	If the contrast value is lover than 4.5 set white as the color of the text.
 */
export const colorTransformer = (
	data: string,
	backgroundColor: string,
	backgroundOverwriteColor?: string
): string => {
	const parser = new DOMParser().parseFromString(data, 'text/html');
	const body = parser.querySelector('body');
	if (!body) return data;
	const elements = body.querySelectorAll('*') as NodeListOf<HTMLElement>;

	elements.forEach(el => {
		let origianlColor = el.style.color;
		if (stringColors[origianlColor]) {
			origianlColor = stringColors[origianlColor];
		}

		const hasBackground = el.style.background || el.style.backgroundColor;

		// some tables has the color property set and overwrite the
		if (el instanceof HTMLTableElement) {
			const originalColor = el.style.color;
			if (originalColor) {
				const contrast = getContrastRatio(origianlColor, backgroundColor);
				if (contrast < 4.3) {
					el.style.removeProperty('color');
				}
			}
		}

		// white td elements with background white needs to be set to background transparent
		if (el instanceof HTMLTableCellElement && hasBackground) {
			if (el.style.background === 'white') {
				el.style.background = 'transparent';
			}
			if (el.style.backgroundColor === 'white') {
				el.style.backgroundColor = 'transparent';
			}
		}

		// There can be span elements that are highlighted and not a color set on them.
		// these will show up as white in darkmode.
		if (el instanceof HTMLSpanElement && hasBackground) {
			if (!el.style.color) {
				el.style.color = 'black';
			}
		}

		// need an extra check on ul elements.
		// these can have a background color set
		if (el instanceof HTMLUListElement && hasBackground) {
			if (el.style.background) {
				el.style.background = backgroundOverwriteColor || '#2A2B2F';
				const contrast = getContrastRatio(origianlColor, backgroundColor);
				if (contrast < 4.3) {
					el.style.color = 'white';
				}
			}
			if (el.style.backgroundColor) {
				// this is the divider color that is used in the pallette
				el.style.backgroundColor = backgroundOverwriteColor || '#2A2B2F';
				const contrast = getContrastRatio(origianlColor, backgroundColor);
				if (contrast < 4.3) {
					el.style.color = 'white';
				}
			}
		}
		// some elements can have color set to inherit.
		if (origianlColor === 'inherit') {
			el.style.color = 'white';
		} else if (!hasBackground && origianlColor) {
			const contrast = getContrastRatio(origianlColor, backgroundColor);
			if (contrast < 4.3) {
				el.style.color = 'white';
			}
		}
	});
	return body.innerHTML;
};

// need this object since the get contrast ratio from mui does not
// handle css string colors.
// White is needed so the highlight does not crash.
export const stringColors: Record<string, string> = {
	black: '#000000',
	blue: '#0000FF',
	darkcyan: '#008B8B',
	darkblue: '#00008B',
	darkgoldenrod: '#B8860B',
	darkgray: '#A9A9A9',
	darkgreen: '#006400',
	darkgrey: '#A9A9A9',
	darkkhaki: '#BDB76B',
	darkmagenta: '#8B008B',
	darkolivegreen: '#556B2F',
	darkorange: '#FF8C00',
	darkorchid: '#9932CC',
	darkred: '#8B0000',
	darksalmon: '#E9967A',
	darkseagreen: '#8FBC8F',
	darkslateblue: '#483D8B',
	darkslategray: '#2F4F4F',
	darkslategrey: '#2F4F4F',
	darkturquoise: '#00CED1',
	darkviolet: '#9400D3',
	green: '#008000',
	indigo: '#4B0082',
	maroon: '#800000',
	midnightblue: '#191970',
	navy: '#000080',
	purple: '#800080',
	red: '#FF0000',
	rebeccapurple: '#663399',
	saddlebrown: '#8B4513',
	sienna: '#A0522D',
	brown: '#A52A2A',
	dimgray: '#696969',
	dimgrey: '#696969',
	mediumblue: '#0000CD',
	mediumvioletred: '#C71585',
	teal: '#008080',
	white: '#FFFFFF',
	yellow: '#FFFF00',
	windowtext: '#000000',
};

export const bulletWithCheckboxTransformer = (
	htmlString: string,
	rapStatus: RapStatus
) => {
	const parser = new DOMParser().parseFromString(htmlString, 'text/html');
	const body = parser.querySelector('body');

	if (!body) return htmlString;

	const listElements = body.querySelectorAll('li');

	listElements.forEach(el => {
		if (el.closest('table') || rapStatus === 'rapstatus-completed') return;

		const initialCheckbox = el.querySelector('input');

		if (initialCheckbox) {
			if (!initialCheckbox.hasAttribute('checked')) {
				// just set default color on list items that does not directly
				// contain a checkbox that is not checked
				el.classList.add('checkbox-default-color');
			}
		}

		if (!el.attributes.getNamedItem('has-eview-checkbox')) {
			el.setAttribute('has-eview-checkbox', '');
			const checkbox = document.createElement('input');
			checkbox.type = 'checkbox';
			checkbox.classList.add('markdown-check-point');
			el.prepend(checkbox);
			// set default color
			el.classList.add('checkbox-default-color');
		}
	});

	return body.innerHTML;
};
